import type React from 'react';
import Decimal from 'decimal.js';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import i18n from './i18n';
import { ValidDateRangeEnd, ValidDateRangeStart, NewTabInvestmentScreeningDetailName, ApplicationPathName } from '../contexts/constants';
import { CompanyPlantSnapshotListType, CompanyPlantSnapshotType, PartNo } from '../contexts/types';
import { TFunction } from 'i18next';
Decimal.set({ precision: 50 });
dayjs.extend(utc);

// 追加ボタン無効時間
export const buttonDisableTime = 500;

/**
 * 現在の言語設定（i18n.language）に応じて日付文字列をロケール別フォーマットで整形する。
 * - ja: "YYYY/MM/DD"（withTime指定時 "YYYY/MM/DD HH:mm:ss"、withSeconds=false時は "YYYY/MM/DD HH:mm"）
 * - en: "MMM D, YYYY"（withTime指定時 "MMM D, YYYY HH:mm:ss"、withSeconds=false時は "MMM D, YYYY HH:mm"）
 * @param date フォーマット対象の日付（文字列 / Date / dayjs 互換値）。null/undefined/不正日付の場合は空文字を返す。
 * @param withTime 時刻（時:分[:秒]）まで含めるかどうか（デフォルト: false）
 * @param withSeconds withTime指定時に秒まで含めるかどうか（デフォルト: true）。withTimeがfalseの場合は無視される。
 * @returns ロケールに応じてフォーマットされた日付文字列
 */
export const formatDateByLocale = (
  date: string | Date | null | undefined,
  withTime: boolean = false,
  withSeconds: boolean = true,
  delimiter: "/" | "-" = "/"
): string => {
  if (!date) return "";
  const d = dayjs(date);
  if (!d.isValid()) return "";

  const isEn = i18n.language === "en";
  const datePattern = isEn ? "MMM D, YYYY" : `YYYY${delimiter}MM${delimiter}DD`;
  const timePattern = withSeconds ? "HH:mm:ss" : "HH:mm";
  const pattern = withTime ? `${datePattern} ${timePattern}` : datePattern;

  return d.format(pattern);
};

/**
 * "YYYY/MM" または "YYYY-MM" 形式の年月文字列を、現在の言語設定（i18n.language）に応じて
 * ロケール別表示に整形する。
 * - ja: "{{year}}/{{month}}"
 * - en: "{{month}}/{{year}}"
 * @param yearMonth 対象の年月文字列（"YYYY/MM" または "YYYY-MM"）。null/undefined/不正値の場合は空文字を返す。
 * @returns ロケールに応じてフォーマットされた年月文字列
 */
export const formatYearMonthByLocale = (yearMonth: string | null | undefined): string => {
  if (!yearMonth) return "";
  const match = yearMonth.match(/^(\d{4})[/-](\d{1,2})$/);
  if (!match) return yearMonth;
  const year = Number(match[1]);
  const month = Number(match[2]);
  if (!year || !month) return yearMonth;

  return i18n.t('common.dateFormat.yearMonthSlash', { year, month, defaultValue: `${year}/${String(month).padStart(2, '0')}` });
};


// utils.ts
// 小数点がない通貨コードのリスト(IRRは将来拡張)
// 日本、韓国、インドネシア、ベトナム、イラン
const noDecimalCurrencies = ['JPY', 'KRW', 'IDR', 'VND', 'IRR', ''];

export const hasDecimal = (currencyName: string): boolean => {
  return !noDecimalCurrencies.includes(currencyName);
};

// 数値フォーマットロジック
// 金額などの数値を小数点第2位 四捨五入 カンマあり文字列で返却する
//const formatNumberLogic = (num: number, currencyName: string): string => {
//  if (hasDecimal(currencyName)) {
//    return num.toLocaleString('en-US', {
//      minimumFractionDigits: 2,
//      maximumFractionDigits: 2,
//    });
//  } else {
//    return Math.round(num).toLocaleString('en-US');
//  }
//};

const formatNumberLogic = (num: Decimal, currencyName: string, decimalPlaces: number = 2): string => {
  if (hasDecimal(currencyName)) {
    const fixedStr = num.toFixed(decimalPlaces);
    const [integerPart, decimalPart] = fixedStr.split('.');
    const formattedIntegerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return decimalPart ? `${formattedIntegerPart}.${decimalPart}` : formattedIntegerPart;
  } else {
    return num.toDecimalPlaces(0, Decimal.ROUND_HALF_UP).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }
};

// 未定義の場合0返却
export const formatNumber = (num: number | Decimal, currencyName: string, decimalPlaces: number = 2): string => {
  if (typeof num !== 'number' && !(num instanceof Decimal) || isNaN(num as number)) {
    return "0";
  }
  const decimalNum = new Decimal(num);
  return formatNumberLogic(decimalNum, currencyName, decimalPlaces);
};

export const formatNumberSpace = (num: number | Decimal, currencyName: string, decimalPlaces: number = 2): string => {
  if (typeof num !== 'number' && !(num instanceof Decimal) || isNaN(num as number)) {
    return "";
  }
  const decimalNum = new Decimal(num);
  return formatNumberLogic(decimalNum, currencyName, decimalPlaces);
};

export const formatNumberHyphen = (num: number | Decimal | string, currencyName: string, decimalPlaces: number = 2): string => {
  if (typeof num !== 'number' && !(num instanceof Decimal) || isNaN(num as number)) {
    return "-";
  }
  const decimalNum = new Decimal(num);
  return formatNumberLogic(decimalNum, currencyName, decimalPlaces);
};

/**
 * 原価合計 数値フォーマット（末尾のゼロを削除）
 *
 * @param {number | Decimal} num フォーマットする数値
 * @param {string} currencyName 通貨名
 * @param {number} maxDecimalPlaces 最大小数点桁数（オプショナル）
 *                                  指定した場合、その桁数で四捨五入し、その桁数まで0を表示
 *                                  未指定の場合、完全な精度で末尾のゼロのみ削除
 * @returns {string} フォーマットされた文字列（カンマ区切り）
 *
 * @example
 * formatCostSum(100.1581, "USD")       // => "100.1581" (末尾のゼロなし)
 * formatCostSum(100.1581, "USD", 2)    // => "100.16" (小数点2桁に四捨五入、2桁まで表示)
 * formatCostSum(100.1500, "USD", 2)    // => "100.15" (小数点2桁、2桁まで表示)
 * formatCostSum(100.0000, "USD", 2)    // => "100.00" (小数点2桁、2桁まで0表示)
 * formatCostSum(100.1581, "USD", 3)    // => "100.158" (小数点3桁に四捨五入、3桁まで表示)
 * formatCostSum(100.1500, "USD", 3)    // => "100.150" (小数点3桁、3桁まで0表示)
 * formatCostSum(100.1581, "JPY")       // => "100" (小数点なし通貨は整数に)
 */
export const formatCostSum = (
  num: number | Decimal,
  currencyName: string,
  maxDecimalPlaces?: number
): string => {
  if (typeof num !== 'number' && !(num instanceof Decimal) || isNaN(num as number)) {
    return "0";
  }
  let decimalNum = new Decimal(num);

  // 小数点なし通貨の場合は整数に丸める
  if (!hasDecimal(currencyName)) {
    const rounded = decimalNum.toDecimalPlaces(0, Decimal.ROUND_HALF_UP);
    return rounded.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  // 最大桁数が指定されている場合は四捨五入してその桁数まで表示
  if (maxDecimalPlaces !== undefined && maxDecimalPlaces >= 0) {
    decimalNum = decimalNum.toDecimalPlaces(maxDecimalPlaces, Decimal.ROUND_HALF_UP);
    // toFixed()で指定桁数まで0を含めて表示
    const fixedStr = decimalNum.toFixed(maxDecimalPlaces);
    // 整数部分にカンマを追加
    const [integerPart, decimalPart] = fixedStr.split('.');
    const formattedIntegerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return decimalPart ? `${formattedIntegerPart}.${decimalPart}` : formattedIntegerPart;
  }

  // 最大桁数が未指定の場合は、末尾のゼロを削除
  const numStr = decimalNum.toString();
  const [integerPart, decimalPart] = numStr.split('.');
  const formattedIntegerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  if (decimalPart !== undefined) {
    // 末尾のゼロを削除
    const trimmedDecimal = decimalPart.replace(/0+$/, '');
    if (trimmedDecimal.length > 0) {
      return `${formattedIntegerPart}.${trimmedDecimal}`;
    }
  }
  return formattedIntegerPart;
};

/**
 * 日付フォーマットチェック
 *
 * @returns Boolean 判定結果 有効：true  無効：false
 */
/**
 * メールアドレス形式チェック
 */
export const isValidEmail = (value: string): boolean => {
  // TLD 2文字以上必須 + 連続ドット禁止
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value) && !value.includes('..');
};

/**
 * カンマ区切り複数メールアドレスの形式チェック
 * 空文字・空白のみは false、カンマ区切りで各アドレスが isValidEmail を満たせば true
 */
export const isValidEmailList = (value: string): boolean => {
  const addresses = value.split(',').map(a => a.trim()).filter(a => a.length > 0);
  if (addresses.length === 0) return false;
  return addresses.every(addr => isValidEmail(addr));
};

export const isValidDateFormat = (dateString: string): boolean => {
  // 日付文字列の形式が "YYYY-MM-DD" であるかをチェック
  const isFormatValid = /^\d{4}-\d{2}-\d{2}$/.test(dateString);

  // 有効な日付であるかをチェック
  const date = new Date(dateString);
  const isDateValid = !isNaN(date.getTime());

  // すべての条件を満たす場合に true(有効日付)
  return isDateValid && isFormatValid;
};

/**
 * 年月日 - 有効範囲チェック
 *
 * @returns Boolean 判定結果 有効：true  無効：false
 */
export const isValidDateRange = (date: Date): boolean => {
  // 年月日が入力可能範囲内であるかチェック　有効：true  無効：false
  return date >= ValidDateRangeStart && date <= ValidDateRangeEnd;
};

/**
 * 1桁月の場合0埋めを行い、区切り文字を"-"に統一する
 * @param value 償却開始月、検収月など（例：2025-9、2025/9）
 * @returns 例：2025-9 ⇒ 2025-09、2025/9 ⇒ 2025-09
 */
export const padYearMonth = (value?: string): string => {
  if (!value) return "";
  // 区切り文字を判定（"/" または "-"）
  const separator = value.includes("/") ? "/" : value.includes("-") ? "-" : null;
  // 区切り文字がない場合はそのまま返す
  if (!separator) return value;
  // 年と月に分ける
  const [year, month] = value.split(separator);
  if (!year || !month) return value;
  // 月が数字かどうかをチェック（異常値対策）
  const monthNum = Number(month);
  if (isNaN(monthNum) || monthNum < 1 || monthNum > 12) return value;
  // 月が1桁なら先頭に0を入れる
  return `${year}-${monthNum.toString().padStart(2, "0")}`;
};

/**
 * 日付値をYYYY-MM形式に正規化・バリデーション
 * yyyy-mm, yyyy-mm-dd, yyyy/mm, yyyy/mm/dd 等の形式を受け付け、YYYY-MMに変換する。
 * isValidDateFormat, isValidDateRange によるチェックを行い、不正な場合は空文字を返す。
 * @param value 検証する値
 * @returns 有効なYYYY-MM形式ならその値、無効なら空文字
 */
export const isValidYearMonth = (value: any): string => {
  if (value === null || value === undefined || value === "") return "";
  // 区切り文字を統一（/ → -）
  const str = String(value).trim().replace(/\//g, "-");
  // YYYY-MM-DD形式ならYYYY-MMに切り詰め
  const normalized = str.replace(/^(\d{4}-\d{2})-\d{2}$/, "$1");
  // YYYY-MM形式チェック
  if (!/^\d{4}-\d{2}$/.test(normalized)) return "";
  // YYYY-MM-DD形式に変換してフォーマットチェック
  const dateStr = `${normalized}-01`;
  if (!isValidDateFormat(dateStr)) return "";
  // 有効範囲チェック
  if (!isValidDateRange(new Date(dateStr))) return "";
  return normalized;
};

/**
 * 日付が指定範囲内かチェック
 * YYYY-MM-DD, YYYY-MM, YYYY/MM/DD, YYYY/MM のいずれの形式にも対応。
 * 区切り文字（/ または -）は内部でハイフンに統一して比較する。
 * YYYY-MM形式の場合、範囲開始日は月初（-01）、範囲終了日は月末（-31）として比較する。
 * @param value チェック対象の日付
 * @param rangeStart 範囲開始日（null/undefinedの場合は下限チェックなし）
 * @param rangeEnd 範囲終了日（null/undefinedの場合は上限チェックなし）
 * @returns 範囲内ならtrue、範囲外ならfalse
 */
export const isDateInRange = (
  value: string,
  rangeStart?: string | null,
  rangeEnd?: string | null,
): boolean => {
  if (!value) return false;

  // 区切り文字を統一（/ → -）
  const normalize = (s: string) => s.replace(/\//g, "-");
  // YYYY-MM形式の場合にDD部分を補完する（開始は月初、終了は月末として扱う）
  const toStartDate = (s: string) => { const n = normalize(s); return n.length === 7 ? `${n}-01` : n; };
  const toEndDate = (s: string) => { const n = normalize(s); return n.length === 7 ? `${n}-31` : n; };

  const target = toStartDate(value);
  if (rangeStart && target < toStartDate(rangeStart)) return false;
  if (rangeEnd && target > toEndDate(rangeEnd)) return false;
  return true;
};

/**
 * 先月のYYYYMMを計算
 *
 * @returns {string} yyyymm
 */
export const getLastMonthYYYYMM = () => {
  const now = new Date();
  const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1); // 先月
  const year = lastMonth.getFullYear();
  const month = String(lastMonth.getMonth() + 1).padStart(2, '0'); // 月を2桁にする
  return `${year}${month}`;
};

/**
 * 指定した時分文字列を時間と分に分割する。
 * @param hourMinuteString 時分文字列(00:00)
 * @returns 時間と分
 */
export const splitHourMinutes = (hourMinuteString: string | undefined): { hours: number, minutes: number } => {
  try {
    if (!hourMinuteString) {
      throw new Error("hourMinuteString is undefined");
    }
    const [hourStr, minuteStr] = hourMinuteString.split(":");
    return {
      hours: parseInt(hourStr, 10),
      minutes: parseInt(minuteStr, 10)
    };
  } catch (error) {
    return {
      hours: 0,
      minutes: 0,
    }
  }
}

/**
 * 時分文字列を分(数値)に変換する。
 * @param hourMinuteString 時分文字列(00:00)
 * @returns 分(数値)
 */
export const hourMinutesStringToMinutes = (hourMinuteString: string | undefined): number => {
  try {
    const { hours, minutes } = splitHourMinutes(hourMinuteString);
    return hours * 60 + minutes;
  } catch (error) {
    return 0;
  }
}

/**
 * 分(数値)を時分文字列(00:00)に変換する
 * @param minutesValue 分(数値)
 * @returns 時分文字列(00:00)
 */
export const minutesToHourMinutesString = (minutesValue: number): string => {
  try {
    const isNegative = minutesValue < 0;
    const absMinutes = Math.abs(minutesValue);

    const hours = Math.floor(absMinutes / 60);
    const minutes = absMinutes % 60;

    const timeString = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    return isNegative ? `-${timeString}` : timeString;
  } catch (error) {
    return "00:00";
  }
}

/**
 * 月次のフィルタリングを行う関数
 * @param year 対象年度
 * @param months 月次データ ({ month: ... })
 * @param startMonth プロジェクト開始月
 * @returns 表示する月のキー配列
 */
export const getVisibleMonths = (year: number, months: { [key: number]: { month: number } }, startMonth: number): number[] => {
  return Object.keys(months).map((key) => Number(key)).filter((monthKey) => {
    const month = months[monthKey]?.month;
    // 初年度でない場合はチェックしない
    if (year !== 1) { return true; }

    if (startMonth >= 1 && startMonth <= 3) {
      // プロジェクト開始月が1～3月の場合
      // 現在の月が1～3月の場合は対象
      return month >= startMonth && month <= 3;
    } else {
      // プロジェクト開始月が4～12月の場合
      // 現在の月が4～12の場合、または1～3の場合は対象
      return (month >= startMonth && month <= 12) || (month >= 1 && month <= 3)
    }
  });
};

/**
 * ソートの判定処理
 * @param x 対象オブジェクト１
 * @param y 対象オブジェクト２
 * @param propertyName プロパティ名
 * @param sortRule asc：昇順、desc：降順
 * @returns 判定結果（-1：昇順なら x が前、0：変更なし、1：昇順なら x が後）
 */
export const compareString = (x: any | undefined, y: any | null | undefined, propertyName: string, sortRule: "asc" | "desc"): number => {
  let a = x?.[propertyName] || null;
  let b = y?.[propertyName] || null;

  if (sortRule === "desc") {
    a = y?.[propertyName] || null;
    b = x?.[propertyName] || null;
  }

  if (!(a || null) && !(b || null)) {
    // 順序の変更なし
    return 0;
  } else if (!(a || null)) {
    // xが後ろになる
    return 1;
  } else if (!(b || null)) {
    // yが後ろになる
    return -1;
  }

  return a.localeCompare(b);
}

/**
 * 指定した値が含まれているかを返す(フィルター用)
 * @param value 対象値
 * @param filterString フィルター文字列
 * @returns true：フィルターに合致する。false：フィルターに合致しない
 */
export const hasFilterString = (value: string | null | undefined, filterString: string | null | undefined): boolean => {
  if (!(filterString || null)) {
    return true;
  }
  return (value ?? '').toLowerCase().includes(filterString?.toLowerCase() || "");
};

/**
 * 文字列 or 文字列配列の値を、表示・ソート・フィルタリング用の単一文字列に変換する
 * (例: equipmentPlanNumberのように、単純な文字列と配列のどちらの形式でも保存され得る項目向け)
 * @param value 対象の値(文字列 or 文字列配列 or null/undefined)
 * @param separator 配列の場合の連結区切り文字(デフォルト: ", ")
 */
export const formatMultiValue = (value: string | string[] | null | undefined, separator: string = ", "): string => {
  if (Array.isArray(value)) {
    return value.filter((v) => !!v).join(separator);
  }
  return value || "";
};

/**
 * 指定した日付が開始日と終了日の範囲内に含まれているかを返す(フィルター用)
 * @param value 対象値
 * @param filterStart フィルター開始日
 * @param filterEnd フィルター終了日
 * @returns true：フィルターに合致する。false：フィルターに合致しない
 */
export const hasFilterDateRange = (value: string | null | undefined, filterStart: Date | null | undefined, filterEnd: Date | null | undefined): boolean => {
  if (!filterStart && !filterEnd) {
    return true;
  }
  if (!value) {
    return false;
  }
  try {
    const parsedDate = new Date(value);
    let result = true;
    if (filterStart) {
      result = filterStart <= parsedDate;
    }
    if (filterEnd) {
      result = result && (parsedDate <= filterEnd);
    }
    return result;
  } catch (e) {
    return false;
  }
};

/**
 * (投資回収チャート用)月から年と月にフォーマットする
 * @param months 月数（何番目の月、1-based）
 */
export const formatRecoveryMonths = (months: number) => {
  // months は「何番目の月」を表す（1-based）
  // 例: 1番目の月 = 0年0ヶ月、13番目の月 = 1年0ヶ月、21番目の月 = 1年8ヶ月
  const index = months - 1;
  const years = Math.floor(index / 12);
  const remainingMonths = index % 12;

  return i18n.t('common.dateFormat.yearMonthPeriod', { year: years, month: remainingMonths, defaultValue: `${years}年${remainingMonths}ヶ月` });
};

/**
 * ファイル名に使用できない文字を全角文字に置換する
 * Windows/Google Driveで禁止されている文字を対応する全角文字に変換
 *
 * @param name サニタイズ対象の文字列
 * @returns サニタイズ後の文字列
 *
 */
export const sanitizeFileName = (name: string): string => {
  return name
    .replace(/\//g, '／')   // スラッシュ → 全角
    .replace(/\\/g, '＼')   // バックスラッシュ → 全角
    .replace(/:/g, '：')    // コロン → 全角
    .replace(/\*/g, '＊')   // アスタリスク → 全角
    .replace(/\?/g, '？')   // クエスチョンマーク → 全角
    .replace(/"/g, '"')     // ダブルクォート → 全角
    .replace(/</g, '＜')    // 小なり → 全角
    .replace(/>/g, '＞')    // 大なり → 全角
    .replace(/\|/g, '｜');  // パイプ → 全角
};

/**
 * UTC日時文字列をユーザーのローカルタイムゾーンで、かつ現在の言語設定（i18n.language）に
 * 応じたロケール別フォーマットで整形する。
 * - ja: "YYYY/MM/DD HH:mm:ss"
 * - en: "MMM D, YYYY HH:mm:ss"
 * `formatDateByLocale` と表記を統一しているため、表示フォーマットが画面間でぶれないこと。
 * @param utcDateString UTC日時文字列 (例: "2026/01/27 23:36:33.631")
 * @returns ローカルタイムゾーン・ロケールでフォーマットされた日時文字列
 */
export const formatLocalDateTime = (utcDateString: string | null | undefined): string => {
  if (!utcDateString) return '';
  // UTCとして解釈し、ブラウザのローカルタイムゾーンに変換
  const localDate = dayjs.utc(utcDateString, "YYYY/MM/DD HH:mm:ss.SSS").local();
  const isEn = i18n.language === "en";
  const pattern = isEn ? "MMM D, YYYY HH:mm:ss" : "YYYY/MM/DD HH:mm:ss";
  return localDate.format(pattern);
};

/**
 * "YYYY-MM-DD" 形式の日付のみ文字列を、タイムゾーンに関わらず
 * その年月日のローカル 00:00:00 を指す Date として構築する。
 * `new Date(dateString)` は当該文字列を UTC 00:00 として解釈するため、
 * 負のUTCオフセット圏で1日ズレる問題があり、直接パースしないこと。
 */
export const parseLocalDateOnly = (
  dateString: string | null | undefined,
): Date | null => {
  if (!dateString) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateString);
  if (!match) return null;
  const [, y, m, d] = match;
  const date = new Date(Number(y), Number(m) - 1, Number(d));
  return isNaN(date.getTime()) ? null : date;
};

/**
 * companyPlantSnapshot JSON文字列を CompanyPlantSnapshotListType にパースする。
 * 後方互換: 旧形式（単一オブジェクト）は items 配列に変換して返す。
 * @param snapshotJson JSON文字列（null/undefined 可）
 * @returns パース結果（複数対応型）または null
 */
export const parseCompanyPlantSnapshot = (
  snapshotJson: string | null | undefined
): CompanyPlantSnapshotListType | null => {
  if (!snapshotJson) return null;
  try {
    const parsed = JSON.parse(snapshotJson);
    // 旧形式（単一オブジェクト）判定：items プロパティが無い
    if (!Array.isArray(parsed?.items)) {
      const single = parsed as CompanyPlantSnapshotType;
      return {
        items: [single],
        activeCurrencyId: single.currencyId ?? '',
        activeTaxRate: single.taxRate ?? null,
        activeAreaCode: single.areaCode ?? '',
      };
    }
    return parsed as CompanyPlantSnapshotListType;
  } catch (e) {
    console.error('Failed to parse companyPlantSnapshot:', e);
    return null;
  }
};

/**
 * companyPlantSnapshot から有効な通貨IDを取得する。
 * 複数選択時はユーザーが選択した有効通貨を返す。
 * @param companyPlantSnapshot JSON文字列
 * @returns 通貨ID（例：'JPY'）または undefined
 */
export const getLocalCurrencyId = (
  companyPlantSnapshot: string | null | undefined
): string | undefined => {
  const snapshot = parseCompanyPlantSnapshot(companyPlantSnapshot);
  return snapshot?.activeCurrencyId || undefined;
};

/**
 * 拠点国を取得する（会社/工場スナップショットから）
 * @param companyPlantSnapshot 会社/工場スナップショットJSON文字列
 * @param isUnique true：重複なし、false：重複あり
 * @returns 拠点国(配列)
 */
export const getLocationCountry = (
  companyPlantSnapshot: string | null | undefined,
  isUnique: boolean = false,
): string[] => {
  const snapshot = parseCompanyPlantSnapshot(companyPlantSnapshot);

  let locationCountries = (snapshot?.items.map(s => s.locationCountry || "") || []);
  if (isUnique) {
    locationCountries = [...new Set(locationCountries.map(l => l ?? "").filter(Boolean))];
  }

  return locationCountries;
};

/**
 * 税率を取得する（会社/工場スナップショットから）
 * @param companyPlantSnapshot 会社/工場スナップショットJSON文字列
 * @returns 税率（存在しない場合はnull）
 */
export const getTaxRateFromSnapshot = (
  companyPlantSnapshot: string | null | undefined
): number | null => {
  const snapshot = parseCompanyPlantSnapshot(companyPlantSnapshot);
  if (snapshot?.activeTaxRate !== null && snapshot?.activeTaxRate !== undefined) {
    return snapshot.activeTaxRate;
  }
  return null;
};

/**
 * 指定したオブジェクト内のグラフデータの凡例ラベルを差し替える
 * @param obj グラフデータを含むオブジェクト
 * @param callback 手動翻訳処理
 * @param t 自動翻訳処理
 * @param datasetIdx データセットインデックス（データセットが複数含まれている場合）
 * @returns void
 */
export const updateChartLabels = (
  obj: any, callback: (label: string, labelIdx: number, datasetIdx: number) => string, t: TFunction, datasetIdx: number) => {
  try {

    if (!obj || typeof obj !== "object") {
      return datasetIdx;
    }

    // datasetsを見つけた場合
    if (Array.isArray(obj.datasets)) {
      obj.datasets.forEach((dataset: any, idx: number) => {
        if ("label" in dataset) {
          // 自動翻訳処理
          const label = t(dataset.label);

          if (label === dataset.label) {
            // 手動翻訳処理（コールバック）
            dataset.label = callback(dataset.label, idx, datasetIdx);
          }
        }
      });
      datasetIdx = datasetIdx + 1;
    }

    // 子要素を再帰探索
    for (const value of Object.values(obj)) {
      datasetIdx = updateChartLabels(value, callback, t, datasetIdx);
    }

    return datasetIdx;

  } catch (error) {
    console.error(`updateChartLabels error.`, error);
    return datasetIdx;
  }
}

// ファイルの最先頭に追加
declare const process: any;

/**
 * React.memo 用の Props 比較ヘルパー（デバッグログ機能付き）
 * 
 * 不一致だった項目をコンソール出力する（デバッグ用）
 * 
 * usage:
 *  const exampleComponent = : React.FC<Props> = memo((props: Props) => {
 *     :
 *   }, (prevProps, nextProps) => isPropsEqual(prevProps, nextProps, 'ExampleComponent')
 *  );
 */
export function isPropsEqual<T extends Record<string, any>>(
  prevProps: T,
  nextProps: T,
  componentName: string = 'Component'
): boolean {
  const prevKeys = Object.keys(prevProps);
  const nextKeys = Object.keys(nextProps);

  // 1. プロパティ数のチェック
  if (prevKeys.length !== nextKeys.length) {
    if (process.env.NODE_ENV !== 'production') {
      console.group(`[React.memo Mismatch] <${componentName}>: Props count changed`);
      console.log('Prev keys:', prevKeys);
      console.log('Next keys:', nextKeys);
      console.groupEnd();
    }
    return false;
  }

  let isAllEqual = true;

  // 2. 各プロパティの不一致をチェックしてログ出力
  for (const key of prevKeys) {
    if (!Object.prototype.hasOwnProperty.call(nextProps, key)) {
      if (process.env.NODE_ENV !== 'production') {
        console.log(`[React.memo Mismatch] <${componentName}>: Prop "${key}" was removed.`);
      }
      isAllEqual = false;
      continue;
    }

    const diff = findDiff(prevProps[key], nextProps[key], key);

    if (diff) {
      if (process.env.NODE_ENV !== 'production') {
        console.group(`[React.memo Mismatch] <${componentName}>: Prop "${diff.path}"`);
        console.log('Reason:', diff.reason);
        console.log('Prev value:', diff.prevValue);
        console.log('Next value:', diff.nextValue);
        console.groupEnd();
      }
      isAllEqual = false;
    }
  }

  return isAllEqual;
}

type DiffResult = {
  path: string;
  reason: string;
  prevValue: any;
  nextValue: any;
} | null;

/**
 * 再帰的に差分を探索し、最初に見つかった不一致の詳細情報を返す
 */
function findDiff(a: any, b: any, currentPath: string): DiffResult {
  // 1. 同一参照
  if (Object.is(a, b)) return null;

  // 2. null / undefined や 型の不一致
  if (a == null || b == null || typeof a !== typeof b) {
    return {
      path: currentPath,
      reason: `Type/Nullability mismatch (${typeof a} vs ${typeof b})`,
      prevValue: a,
      nextValue: b,
    };
  }

  // 3. 関数（参照不一致）
  if (typeof a === 'function') {
    return {
      path: currentPath,
      reason: 'Function reference changed (Unmemoized function passed?)',
      prevValue: a,
      nextValue: b,
    };
  }

  // 4. Decimal.js などの .equals() メソッド
  if (
    typeof a === 'object' &&
    typeof a.equals === 'function' &&
    typeof b.equals === 'function'
  ) {
    if (!a.equals(b)) {
      return {
        path: currentPath,
        reason: 'Decimal values are not equal',
        prevValue: a,
        nextValue: b,
      };
    }
    return null;
  }

  // 5. Date オブジェクト
  if (a instanceof Date && b instanceof Date) {
    if (a.getTime() !== b.getTime()) {
      return {
        path: currentPath,
        reason: 'Date values are not equal',
        prevValue: a,
        nextValue: b,
      };
    }
    return null;
  }

  // 6. 配列
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) {
      return {
        path: currentPath,
        reason: `Array length mismatch (${a.length} vs ${b.length})`,
        prevValue: a,
        nextValue: b,
      };
    }
    for (let i = 0; i < a.length; i++) {
      const diff = findDiff(a[i], b[i], `${currentPath}[${i}]`);
      if (diff) return diff;
    }
    return null;
  }

  // 7. プレーンオブジェクト
  if (typeof a === 'object' && typeof b === 'object') {
    const keysA = Object.keys(a);
    const keysB = Object.keys(b);

    if (keysA.length !== keysB.length) {
      return {
        path: currentPath,
        reason: `Object keys count mismatch (${keysA.length} vs ${keysB.length})`,
        prevValue: a,
        nextValue: b,
      };
    }

    for (const key of keysA) {
      if (!Object.prototype.hasOwnProperty.call(b, key)) {
        return {
          path: `${currentPath}.${key}`,
          reason: 'Property missing in next value',
          prevValue: a[key],
          nextValue: undefined,
        };
      }
      const diff = findDiff(a[key], b[key], `${currentPath}.${key}`);
      if (diff) return diff;
    }
    return null;
  }

  return {
    path: currentPath,
    reason: 'Primitive values not equal',
    prevValue: a,
    nextValue: b,
  };
}

/**
 * カンマ区切りで複数の値を1つの文字列に結合する際、値自体に含まれる
 * バックスラッシュ・カンマをエスケープ（区切り文字と衝突しないよう退避）してから結合する。
 * splitEscapedList() と対で使うことで、値の中に含まれるカンマと
 * 区切り用のカンマを区別し、値を壊さずに結合・復元できる。
 *
 * 例: ["ABC Corporation, Inc.", "XYZ商事"]
 *   → "ABC Corporation\\, Inc.,XYZ商事"
 *
 * @param values 結合する値のリスト（null/undefined/空文字は除外される）
 * @returns カンマ区切りの1つの文字列
 */
export const joinEscapedList = (values: (string | null | undefined)[]): string => {
  return values
    .filter((v): v is string => !!v && v.trim() !== "")
    .map((v) =>
      v
        .split("\\").join("\\\\") // まずバックスラッシュ自体をエスケープ
        .split(",").join("\\,")   // 次にカンマ（区切り文字）をエスケープ
    )
    .join(",");
};

/**
 * joinEscapedList() で結合された文字列を、元の値のリストに復元する。
 * バックスラッシュでエスケープされていないカンマのみを区切りとして分割し、
 * 各値のエスケープを解除・前後の空白を除去して返す。
 * エスケープされていない単純なカンマ区切り文字列（旧データ）にも後方互換で対応する。
 *
 * @param raw joinEscapedList() で結合された文字列
 * @returns 分割・復元後の値のリスト（rawが空の場合は空配列）
 */
export const splitEscapedList = (raw: string | null | undefined): string[] => {
  if (!raw) return [];

  const result: string[] = [];
  let current = "";

  for (let i = 0; i < raw.length; i++) {
    const ch = raw[i];
    if (ch === "\\" && i + 1 < raw.length) {
      // 次の1文字をそのまま値として取り込む（エスケープ解除）
      current += raw[i + 1];
      i++;
    } else if (ch === ",") {
      result.push(current.trim());
      current = "";
    } else {
      current += ch;
    }
  }
  result.push(current.trim());

  return result;
};

/**
 * 検索系一覧画面（Home、InputList等）の投資審査詳細画面へのリンクで、
 * Ctrl+クリック・Cmd+クリック（Mac）・中クリックを検知した場合のみ別タブで開く。
 * 通常クリックの場合は何もせず、既存の <Link as={RouterLink}> による同一タブ遷移に任せる。
 *
 * 別タブは window.open で先に空タブを開き、そのタブ固有の sessionStorage に
 * projectId/reviewDateを書き込んでから遷移させる方式（既存のサブウィンドウ方式を応用）。
 * これにより親タブのsessionStorageを一切参照せず、別タブ間の案件混同を防ぐ。
 *
 * @param event Linkのクリックイベント（onClick）またはマウスイベント（onAuxClick、中クリック用）
 * @param projectId 遷移先で表示する案件のprojectId
 * @param reviewDate 振り返りモードで開く場合の対象日（通常はundefined）
 */
export const openInvestmentScreeningDetailInNewTab = (
  event: React.MouseEvent,
  projectId: string,
  reviewDate?: string | null,
): void => {
  // 中クリック（onAuxClickではbutton===1固定だが明示チェック）、Ctrl/Cmdクリックのみ対象
  const isMiddleClick = event.type === "auxclick" && event.button === 1;
  const isModifiedClick = event.type === "click" && (event.ctrlKey || event.metaKey);
  if (!isMiddleClick && !isModifiedClick) return;

  event.preventDefault();

  // window.open は非同期処理の前に同期実行（ポップアップブロック回避）
  const newWindow = window.open("", "_blank");
  if (!newWindow) return;

  // ウィンドウ名：別タブ表示であることを通知するため設定（サブウィンドウとは別の識別子）
  newWindow.name = NewTabInvestmentScreeningDetailName;
  newWindow.sessionStorage.setItem("newTabMode", "1");
  // newWindow.sessionStorage.setItem("newTabProjectId", projectId || "");
  newWindow.sessionStorage.setItem("projectId", projectId || "");
  if (reviewDate) {
    newWindow.sessionStorage.setItem("reviewDate", reviewDate);
  }

  newWindow.location.href = `${window.origin}${ApplicationPathName.InvestmentScreeningDetail.Url}`;
};

/**
 * PartNoの画面表示用文字列を解決する（No.435対応）
 * BEはpartNo（実キー）とdisplayPartNo（変更後PartNo、未登録ならnull/空）を別々に返す。
 * 「表示名がなければ実キーを見せる」というフォールバック判定はUI側の関心事のためここに集約する。
 * @param partNo PartNoオブジェクト（またはpartNo/displayPartNoのみを持つ部分オブジェクト）
 * @returns 表示用のPartNo文字列。いずれも未設定の場合は空文字
 */
export const resolveDisplayPartNo = (
  partNo?: Pick<PartNo, "partNo" | "displayPartNo"> | null
): string => {
  return partNo?.displayPartNo || partNo?.partNo || "";
};

/**
 * partNo（実キー文字列）から、PartNo一覧を突き合わせて画面表示用文字列を解決する（No.435対応）
 * cycle_time_in_second_by_part_no等、JSON内にpartNo（実キー）のみを保持する構造の表示変換に使用する。
 * @param key 実キーのPartNo文字列
 * @param partNoList displayPartNoを含むPartNo一覧（getPartNo等のレスポンス）
 * @returns 表示用のPartNo文字列。一覧に見つからない場合はkeyをそのまま返す
 */
export const resolveDisplayPartNoByKey = (
  key: string | undefined | null,
  partNoList: Pick<PartNo, "partNo" | "displayPartNo">[] | undefined | null
): string => {
  if (!key) return "";
  const found = partNoList?.find(p => p.partNo === key);
  return found ? resolveDisplayPartNo(found) : key;
};

/**
 * Simulation画面で保存されたチャートデータセットのlabel文字列内に含まれる
 * PartNo実キー部分を、displayPartNoに変換する（No.435対応、既存保存データのフォールバック用）。
 * chartDataCalculation.ts修正前に保存されたJSON（labelに実キーがそのまま焼き付いている）を
 * 表示時に補正するために使用する。修正後に保存されたJSON（既にdisplayPartNoベース）は
 * 抽出したkeyがPartNo一覧のpartNo（実キー）と一致しないため変換されず素通りする。よって二重変換は発生しない。
 * @param chartData Chart.js用データ（{ labels, datasets: [{ label, partNoKey?, ... }] }）
 * @param partNoList displayPartNoを含むPartNo一覧（getPartNo等のレスポンス）
 * @returns labelを補正した新しいchartDataオブジェクト（非破壊）
 */
export function resolveChartDatasetLabels<T extends { labels?: string[]; datasets: any[] }>(
  chartData: T,
  partNoList: Pick<PartNo, "partNo" | "displayPartNo">[] | undefined | null
): T;
export function resolveChartDatasetLabels(
  chartData: { labels?: string[]; datasets?: any[] } | null | undefined,
  partNoList: Pick<PartNo, "partNo" | "displayPartNo">[] | undefined | null
): typeof chartData;
export function resolveChartDatasetLabels(
  chartData: { labels?: string[]; datasets?: any[] } | null | undefined,
  partNoList: Pick<PartNo, "partNo" | "displayPartNo">[] | undefined | null
) {
  if (!chartData?.datasets || !partNoList || partNoList.length === 0) return chartData;

  return {
    ...chartData,
    datasets: chartData.datasets.map((d: any) => {
      if (typeof d.label !== "string") return d;
      // partNoKeyがあればそれを優先的に使用、なければlabel文字列からPart:xxx部分を抽出
      const key = d.partNoKey ?? d.label.match(/^Part:(\S+)/)?.[1];
      if (!key) return d;
      const found = partNoList.find(p => p.partNo === key);
      if (!found || !found.displayPartNo) return d;
      return { ...d, label: d.label.replace(`Part:${key}`, `Part:${found.displayPartNo}`) };
    }),
  };
}